/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  SectionTeacherProjectTeachingTime,
  SectionTeacherRow,
  TeacherOption,
  TeacherWorkloadMode,
  TeachingRangeOption,
} from 'src/app/services/models/subjects/subject.model';
import {
  ExamPeriodWindows,
  countDayOfWeekOccurrences,
  formatSelectedTeachingRanges,
  formatTeachingDate,
  getTeachingRangeIdentity,
  isExamPeriodDate,
  isExamPeriodRange,
  normalizeTeachingRangeSelection,
  parseDateOnly,
  teachingRangeMatchesTime,
  toMinute,
  toNullableString,
} from './subject-management-create.utils';

export interface TeachingTimeValue {
  teachingDay: unknown;
  startTime: unknown;
  endTime: unknown;
}

export interface TeachingRangeBuilderInput {
  teachingDays: unknown[];
  teachingTimes?: TeachingTimeValue[];
  studyStartDate?: string | null;
  studyEndDate?: string | null;
  midtermStartDate?: string | null;
  midtermEndDate?: string | null;
  finalStartDate?: string | null;
  finalEndDate?: string | null;
  fallbackRanges?: unknown[];
}

export interface RegularTeacherRowBuilderInput {
  draft: {
    userId: unknown;
    fullName: unknown;
    department: unknown;
    teachingRange: unknown;
  };
  teacherOptions: TeacherOption[];
  workloadHours: unknown;
  workloadMode: TeacherWorkloadMode;
  teachingTimes: TeachingTimeValue[];
}

export interface ProjectTeacherRowBuilderInput {
  formValue: {
    userId: unknown;
    department: unknown;
    workloadHours: unknown;
    primaryStudentCount: unknown;
    coStudentCount: unknown;
    description: unknown;
    showInSchedule: unknown;
  };
  teacherOptions: TeacherOption[];
  teachingTimes: TeachingTimeValue[];
}

const THAI_DAY_INDEXES: Partial<Record<string, number>> = {
  'วันอาทิตย์': 0,
  'วันจันทร์': 1,
  'วันอังคาร': 2,
  'วันพุธ': 3,
  'วันพฤหัสบดี': 4,
  'วันศุกร์': 5,
  'วันเสาร์': 6,
};

const THAI_DAY_SORT_ORDER: Partial<Record<string, number>> = {
  'วันจันทร์': 0,
  'วันอังคาร': 1,
  'วันพุธ': 2,
  'วันพฤหัสบดี': 3,
  'วันศุกร์': 4,
  'วันเสาร์': 5,
  'วันอาทิตย์': 6,
};

export function buildTeachingRangeOptions(input: TeachingRangeBuilderInput): TeachingRangeOption[] {
  const startDate = parseDateOnly(input.studyStartDate);
  const endDate = parseDateOnly(input.studyEndDate);
  const examWindows: ExamPeriodWindows = {
    midtermStartDate: input.midtermStartDate,
    midtermEndDate: input.midtermEndDate,
    finalStartDate: input.finalStartDate,
    finalEndDate: input.finalEndDate,
  };
  // Fallback ranges (e.g. already-selected values carried over from a previous
  // save) must also skip exam-window dates — otherwise a date chosen before the
  // exam window existed, or before this exclusion rule shipped, resurfaces as a
  // selectable option every time the dropdown is rebuilt.
  const fallbackOptions = toTeachingRangeOptions(input.fallbackRanges ?? [], examWindows);

  if (startDate == null || endDate == null || startDate > endDate) {
    return fallbackOptions;
  }

  const teachingDays = Array.from(new Set(input.teachingDays
    .map((day) => toNullableString(day))
    .filter((day): day is string => day != null)
    .filter((day) => THAI_DAY_INDEXES[day] != null)));
  const generatedOptions = teachingDays
    .flatMap((day) => {
      const timeLabels = getTeachingTimeLabelsForDay(day, input.teachingTimes ?? []);
      const labels = timeLabels.length > 0 ? timeLabels : [null];

      return buildTeachingRangeLabelsForDay(day, startDate, endDate, labels, examWindows);
    })
    .map((label) => ({ value: label, label }));

  return mergeTeachingRangeOptions(fallbackOptions, generatedOptions);
}

export function mergeTeachingRangeOptions(...groups: TeachingRangeOption[][]): TeachingRangeOption[] {
  const optionsByValue = new Map<string, TeachingRangeOption>();

  groups.flat().forEach((option) => {
    const identity = getTeachingRangeIdentity(option.value);

    if (identity !== '' && !optionsByValue.has(identity)) {
      optionsByValue.set(identity, option);
    }
  });

  return Array.from(optionsByValue.values());
}

export function normalizeTeachingRangeValuesToOptions(
  value: unknown,
  options: TeachingRangeOption[]
): string[] {
  const optionValuesByIdentity = new Map(
    options.map((option) => [getTeachingRangeIdentity(option.value), option.value])
  );
  const normalizedValues = new Map<string, string>();

  normalizeTeachingRangeSelection(value).forEach((range) => {
    const identity = getTeachingRangeIdentity(range);
    normalizedValues.set(identity, optionValuesByIdentity.get(identity) ?? range);
  });

  return Array.from(normalizedValues.values());
}

export function buildRegularTeacherRow(input: RegularTeacherRowBuilderInput): SectionTeacherRow | null {
  const userId = toNumberOrNull(input.draft.userId);

  if (userId == null) {
    return null;
  }

  const teacher = input.teacherOptions.find((option) => option.id === userId);

  return {
    userId,
    fullName: teacher?.fullName ?? toNullableString(input.draft.fullName) ?? '-',
    department: teacher?.department ?? toNullableString(input.draft.department) ?? '-',
    teachingRange: formatSelectedTeachingRanges(input.draft.teachingRange),
    workloadHours: calculateRegularTeacherWorkloadHours(
      input.draft.teachingRange,
      input.teachingTimes,
      input.workloadHours,
      input.workloadMode
    ),
    updatedAt: '-',
    updatedBy: '-',
    status: '-',
    returnReason: '-',
  };
}

export function calculateRegularTeacherWorkloadHours(
  teachingRange: unknown,
  teachingTimes: TeachingTimeValue[],
  workloadHours: unknown,
  workloadMode: TeacherWorkloadMode
): string {
  const workloadHoursValue = Number(workloadHours);

  if (!Number.isFinite(workloadHoursValue) || workloadHoursValue < 0) {
    return '0';
  }

  const workloadLimit = workloadMode === 'shared' ? workloadHoursValue / 2 : workloadHoursValue;
  const selectedRanges = Array.from(new Map(
    normalizeTeachingRangeSelection(teachingRange).map((range) => [getTeachingRangeIdentity(range), range])
  ).values());
  const totalHours = selectedRanges.reduce((total, range) => {
    const hoursForRange = teachingTimes.reduce((sum, time) => {
      const day = toNullableString(time.teachingDay);
      const startTime = toNullableString(time.startTime);
      const endTime = toNullableString(time.endTime);
      const start = toMinute(time.startTime);
      const end = toMinute(time.endTime);

      if (
        day == null
        || startTime == null
        || endTime == null
        || start == null
        || end == null
        || start >= end
      ) {
        return sum;
      }

      const dayOfWeek = THAI_DAY_INDEXES[day];

      return dayOfWeek != null && teachingRangeMatchesTime(range, dayOfWeek, startTime, endTime)
        ? sum + ((end - start) / 60)
        : sum;
    }, 0);

    return total + hoursForRange;
  }, 0);

  return formatHours(Math.min(totalHours, workloadLimit));
}

export function buildProjectTeacherRow(input: ProjectTeacherRowBuilderInput): SectionTeacherRow | null {
  const userId = toNumberOrNull(input.formValue.userId);

  if (userId == null) {
    return null;
  }

  const teacher = input.teacherOptions.find((option) => option.id === userId);
  const projectTeachingTimes = normalizeProjectTeachingTimes(input.teachingTimes);

  return {
    userId,
    fullName: teacher?.fullName ?? '-',
    department: teacher?.department ?? toNullableString(input.formValue.department) ?? '-',
    teachingRange: formatTeachingTimeRange(input.teachingTimes),
    workloadHours: toNullableString(input.formValue.workloadHours) ?? '0',
    updatedAt: '-',
    updatedBy: '-',
    status: '-',
    returnReason: '-',
    projectTeachingTimes,
    primaryAdvisorStudentCount: toNullableString(input.formValue.primaryStudentCount),
    coAdvisorStudentCount: toNullableString(input.formValue.coStudentCount),
    description: toNullableString(input.formValue.description) ?? '',
    showInTimetable: input.formValue.showInSchedule === true,
  };
}

export function calculateProjectWorkloadHours(
  studyStartDate: string | null | undefined,
  studyEndDate: string | null | undefined,
  teachingTimes: TeachingTimeValue[],
  examWindows?: ExamPeriodWindows | null
): string {
  const startDate = parseDateOnly(studyStartDate);
  const endDate = parseDateOnly(studyEndDate);

  if (startDate == null || endDate == null || startDate > endDate) {
    return '0';
  }

  const totalHours = teachingTimes.reduce((sum, time) => {
    const day = toNullableString(time.teachingDay);
    const dayOfWeek = day == null ? null : THAI_DAY_INDEXES[day] ?? null;
    const start = toMinute(time.startTime);
    const end = toMinute(time.endTime);

    if (dayOfWeek == null || start == null || end == null || start >= end) {
      return sum;
    }

    return sum + (countDayOfWeekOccurrences(startDate, endDate, dayOfWeek, examWindows) * ((end - start) / 60));
  }, 0);

  return formatHours(totalHours);
}

export function normalizeProjectTeachingTimes(
  teachingTimes: TeachingTimeValue[]
): SectionTeacherProjectTeachingTime[] {
  return sortProjectTeachingTimes(teachingTimes.map((time) => ({
    teachingDay: toNullableString(time.teachingDay),
    startTime: toNullableString(time.startTime),
    endTime: toNullableString(time.endTime),
  })));
}

export function sortProjectTeachingTimes(
  teachingTimes: SectionTeacherProjectTeachingTime[]
): SectionTeacherProjectTeachingTime[] {
  return teachingTimes
    .map((time, index) => ({ time, index, sortKey: getProjectTeachingTimeSortKey(time) }))
    .sort((current, next) => {
      if (current.sortKey == null && next.sortKey == null) {
        return current.index - next.index;
      }

      if (current.sortKey == null) {
        return 1;
      }

      if (next.sortKey == null) {
        return -1;
      }

      if (current.sortKey.day !== next.sortKey.day) {
        return current.sortKey.day - next.sortKey.day;
      }

      if (current.sortKey.start !== next.sortKey.start) {
        return current.sortKey.start - next.sortKey.start;
      }

      return current.index - next.index;
    })
    .map(({ time }) => time);
}

function formatTeachingTimeRange(teachingTimes: TeachingTimeValue[]): string {
  const ranges = normalizeProjectTeachingTimes(teachingTimes)
    .map((time) => {
      if (time.teachingDay == null) {
        return '';
      }

      return time.startTime != null && time.endTime != null
        ? `${time.teachingDay} ${time.startTime} - ${time.endTime}`
        : time.teachingDay;
    })
    .filter((range) => range !== '');

  return ranges.length > 0 ? ranges.join(', ') : '-';
}

function toTeachingRangeOptions(
  values: unknown[],
  examWindows?: ExamPeriodWindows | null
): TeachingRangeOption[] {
  return mergeTeachingRangeOptions(values
    .flatMap((value) => normalizeTeachingRangeSelection(value))
    .filter((label) => !isExamPeriodRange(label, examWindows))
    .map((label) => ({ value: label, label })));
}

function getTeachingTimeLabelsForDay(teachingDay: string, teachingTimes: TeachingTimeValue[]): string[] {
  return Array.from(new Set(teachingTimes
    .filter((time) => toNullableString(time.teachingDay) === teachingDay)
    .map((time) => {
      const startTime = toNullableString(time.startTime);
      const endTime = toNullableString(time.endTime);

      return startTime != null && endTime != null ? `${startTime} - ${endTime}` : null;
    })
    .filter((label): label is string => label != null)));
}

function buildTeachingRangeLabelsForDay(
  teachingDay: string,
  startDate: Date,
  endDate: Date,
  teachingTimeLabels: (string | null)[],
  examWindows?: ExamPeriodWindows | null
): string[] {
  const dayOfWeek = THAI_DAY_INDEXES[teachingDay];
  const currentDate = new Date(startDate);
  const ranges: string[] = [];
  let occurrence = 1;

  if (dayOfWeek == null) {
    return [];
  }

  while (currentDate <= endDate) {
    // Exam-period dates are skipped and do not consume an occurrence number, so
    // the "ที่ N" sequence stays contiguous over actual teaching days — matching
    // the backend teaching-date generators that also exclude exam windows.
    if (currentDate.getDay() === dayOfWeek && !isExamPeriodDate(currentDate, examWindows)) {
      for (const teachingTimeLabel of teachingTimeLabels) {
        const timeLabel = teachingTimeLabel == null ? '' : ` (${teachingTimeLabel})`;

        ranges.push(`${teachingDay}ที่ ${occurrence}${timeLabel} - ${formatTeachingDate(currentDate)}`);
      }
      occurrence += 1;
    }

    currentDate.setDate(currentDate.getDate() + 1);
  }

  return ranges;
}

function toNumberOrNull(value: unknown): number | null {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
}

function formatHours(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function getProjectTeachingTimeSortKey(time: SectionTeacherProjectTeachingTime): { day: number; start: number } | null {
  const day = time.teachingDay == null ? null : THAI_DAY_SORT_ORDER[time.teachingDay] ?? null;
  const start = toMinute(time.startTime);

  return day == null || start == null ? null : { day, start };
}
